import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, EmptyState, Input, useToast } from '@adop/ui';
import { Search, Store } from 'lucide-react';
import { formatCents } from '@adop/shared';
import type { RestaurantStatus } from '@adop/shared';
import type { AdminRestaurantRow } from '@adop/shared/admin';
import {
  fetchAdminRestaurants,
  setAdminRestaurantStatus,
} from '../../lib/admin-api';
import { errorMessage } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { formatDate, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';
import { AdminPagination } from './AdminPagination';
import { AdminRestaurantStatusBadge } from './AdminRestaurantStatusBadge';
import { AdminStatusModal } from './AdminStatusModal';

const PAGE_SIZE = 25;

export function AdminRestaurantsPage() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const [page, setPage] = useState(1);
  const debouncedQuery = useDebouncedValue(query, 300);

  const [target, setTarget] = useState<AdminRestaurantRow | null>(null);
  const [nextStatus, setNextStatus] = useState<RestaurantStatus>('suspended');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const state = useAsync(
    () =>
      fetchAdminRestaurants({
        q: debouncedQuery.trim() || undefined,
        page,
        page_size: PAGE_SIZE,
      }),
    [debouncedQuery, page],
  );

  const openAction = (restaurant: AdminRestaurantRow) => {
    setTarget(restaurant);
    setNextStatus(restaurant.status === 'active' ? 'suspended' : 'active');
    setActionError(null);
  };

  const confirmStatus = async (reason: string) => {
    if (!target) return;
    setBusy(true);
    setActionError(null);
    try {
      await setAdminRestaurantStatus(target.id, {
        status: nextStatus,
        ...(reason.length > 0 ? { reason } : {}),
      });
      toast({
        title: nextStatus === 'suspended' ? 'Restaurant suspended' : 'Restaurant reactivated',
        variant: 'success',
      });
      setTarget(null);
      state.reload();
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const restaurants = state.data?.restaurants ?? [];
  const total = state.data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Restaurants"
        description="Every restaurant on the platform. Open one for the audited support view."
      />

      {state.error ? <Alert>{state.error}</Alert> : null}

      <Card>
        <div className="border-b border-zinc-200 p-4">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              aria-label="Search restaurants"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name"
              className="pl-9"
            />
          </div>
        </div>

        {state.loading && !state.data ? (
          <LoadingBlock />
        ) : restaurants.length === 0 ? (
          <EmptyState
            icon={<Store className="h-6 w-6" />}
            title={debouncedQuery.trim() ? 'No matching restaurants' : 'No restaurants yet'}
            description={
              debouncedQuery.trim()
                ? 'Try a different search term.'
                : 'Restaurants appear here once someone registers.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[60rem] text-left text-sm">
                <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Plan</th>
                    <th className="px-4 py-2 font-medium">Stripe</th>
                    <th className="px-4 py-2 text-right font-medium">Locations</th>
                    <th className="px-4 py-2 text-right font-medium">Members</th>
                    <th className="px-4 py-2 text-right font-medium">Orders (30d)</th>
                    <th className="px-4 py-2 text-right font-medium">Revenue (30d)</th>
                    <th className="px-4 py-2 font-medium">Created</th>
                    <th className="px-4 py-2 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {restaurants.map((restaurant) => (
                    <tr key={restaurant.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/restaurants/${restaurant.id}`}
                          className="font-medium text-zinc-900 underline decoration-zinc-300 hover:decoration-zinc-900"
                        >
                          {restaurant.name}
                        </Link>
                        {restaurant.last_order_at ? (
                          <p className="text-xs text-zinc-500">
                            Last order {relativeTime(restaurant.last_order_at)}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <AdminRestaurantStatusBadge status={restaurant.status} />
                      </td>
                      <td className="px-4 py-3 capitalize text-zinc-600">{restaurant.plan}</td>
                      <td className="px-4 py-3 text-zinc-600">
                        {restaurant.stripe_charges_enabled ? 'Enabled' : 'Not enabled'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-zinc-700">
                        {restaurant.published_location_count}/{restaurant.location_count}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-zinc-700">
                        {restaurant.member_count}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-zinc-700">
                        {restaurant.orders_30d}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-zinc-700">
                        {formatCents(restaurant.revenue_30d_cents, { showCents: false })}
                      </td>
                      <td className="px-4 py-3 text-zinc-600">{formatDate(restaurant.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant={restaurant.status === 'active' ? 'danger' : 'outline'}
                          size="sm"
                          onClick={() => openAction(restaurant)}
                        >
                          {restaurant.status === 'active' ? 'Suspend' : 'Reactivate'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              unit="restaurants"
            />
          </>
        )}
      </Card>

      <AdminStatusModal
        open={target !== null}
        restaurantName={target?.name ?? ''}
        nextStatus={nextStatus}
        busy={busy}
        error={actionError}
        onClose={() => {
          if (!busy) setTarget(null);
        }}
        onConfirm={(reason) => void confirmStatus(reason)}
      />
    </div>
  );
}
